import type { PostHogErrorBoundaryFallbackProps } from '@posthog/react';
import { Button } from '../ui/button.js';

function isChunkLoadFailure(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('ChunkLoadError') ||
    error.message.includes('Unable to preload CSS for')
  );
}

export default function AppCrashFallback({ error }: PostHogErrorBoundaryFallbackProps) {
  const chunkLoadFailure = isChunkLoadFailure(error);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-950 px-6 text-slate-50">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/95 p-8 shadow-2xl">
        <p className="text-sm font-medium uppercase tracking-[0.22em] text-amber-300">
          Application Error
        </p>
        <h1 className="mt-3 text-base font-semibold text-white">
          {chunkLoadFailure
            ? 'The app has an outdated cached file.'
            : 'Something broke in the dashboard.'}
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          {chunkLoadFailure
            ? 'A new version was deployed while this tab was open. Reload the app to fetch the latest files.'
            : 'Reload the app to retry from a clean state.'}
        </p>
        {error instanceof Error ? (
          <p className="mt-4 rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-300">
            {error.message}
          </p>
        ) : null}
        <Button className="mt-6 w-full" onClick={() => window.location.reload()}>
          {chunkLoadFailure ? 'Load latest version' : 'Reload app'}
        </Button>
      </div>
    </div>
  );
}
